



import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CallIcon from "@mui/icons-material/Call";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ApiCallService from "../../../Service/ApiCallService";
import dummyImage from "../../../images/user.png";
import Spinner from "../../../components/common/Spinner";
import { getPublicS3Url } from "../../../utils/s3PublicUrl";
import CallFollowUpModal from "./CallFollowUpModal";
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';
import { ArrowForward, ArrowRight } from "@mui/icons-material";

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value.$oid === "string") return value.$oid;
    if (value._id) return getId(value._id);
  }
  return "";
};

const getDesignationText = (member) => {
  if (Array.isArray(member?.designations)) {
    return member.designations
      .map((designation) => designation?.title || designation?.name || "")
      .filter(Boolean)
      .join(", ");
  }

  return member?.position || member?.designation || "";
};

const normalizeMember = (member) => {
  const user = member?.user || member?.userId || member;
  const committee = member?.committee || {};
  const committeeType = committee?.committeeType || member?.committeeType || {};
  const council = committeeType?.councilId || member?.council || {};

  return {
    _id: getId(user?._id) || getId(member?._id),
    userId: getId(user?._id) || getId(member?.userId) || getId(member?._id),
    userFirstName: user?.userFirstName || member?.userFirstName || "",
    userLastName: user?.userLastName || member?.userLastName || "",
    userImageURL: user?.userImageURL || member?.userImageURL || "",
    mobileNumber: user?.mobileNumber || member?.mobileNumber || "",
    email: user?.email || member?.email || "",
    position: getDesignationText(member),
    committeeType:
      committeeType?.name ||
      member?.committeeType?.name ||
      member?.committeeType ||
      "",
    council:
      council?.councilName ||
      member?.council?.councilName ||
      member?.council ||
      "",
  };
};

const getMembersFromResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.members)) return response.members;
  return [];
};

const sortOptions = [
  { value: "nameAsc", label: "Name A-Z" },
  { value: "nameDesc", label: "Name Z-A" },
];

const LAST_CONTACT_ALL = "ALL";

const staleDayOptions = [
  { label: "All members", value: LAST_CONTACT_ALL },
  { label: "Not connected in 10 days", value: 10 },
  { label: "Not connected in 20 days", value: 20 },
  { label: "Not connected in 30 days", value: 30 },
];

const committeeSections = [
  {
    key: "Founder",
    title: "Founder",
    aliases: ["founder"],
  },
  {
    key: "governing",
    title: "Governing Committee",
    aliases: ["governing committee", "governing"],
  },
  {
    key: "operations",
    title: "Operations Committee",
    aliases: ["operations committee", "operation committee", "operations"],
  },
  {
    key: "nationalProgramHead",
    title: "National Program Head",
    aliases: ["national program head", "national programme head"],
  },
  {
    key: "stateLeaders",
    title: "State Presidents & State General Secretaries",
    aliases: [
      "state presidents state general secretaries",
      "state president state general secretary",
      "state presidents",
      "state general secretaries",
      "state president",
      "state general secretary",
    ],
  },
];

const normalizeText = (value = "") =>
  value
    .toString()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getMemberSectionKey = (member) => {
  const text = normalizeText(
    [member.committeeType, member.position, member.council].filter(Boolean).join(" ")
  );

  const section = committeeSections.find(({ aliases }) =>
    aliases.some((alias) => text.includes(normalizeText(alias)))
  );

  return section?.key || "other";
};

const getInitials = (firstName = "", lastName = "") =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "M";

const getMemberKey = (member) => member?.userId || member?._id || "";
const getSelectableMemberIds = (items = []) =>
  items.map(getMemberKey).filter(Boolean);

const sameMemberSet = (left = [], right = []) => {
  const leftSet = new Set(left.filter(Boolean));
  const rightSet = new Set(right.filter(Boolean));

  return (
    leftSet.size > 0 &&
    leftSet.size === rightSet.size &&
    [...leftSet].every((id) => rightSet.has(id))
  );
};

const getLastContactValue = (member = {}) =>
  member.lastContactedAt ||
  member.lastContactDate ||
  member.lastContactedDate ||
  member.lastConnectedAt ||
  member.lastConnectedDate ||
  member.lastFollowUpAt ||
  member.lastFollowUpDate ||
  member.dateTime ||
  "";

const getDateFromDaysSinceContact = (daysSinceContact) => {
  if (daysSinceContact == null || daysSinceContact === "") return null;

  const days = Number(daysSinceContact);
  if (!Number.isFinite(days)) return null;

  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return date;
};

const formatLastContact = (value, daysSinceContact) => {
  const directDate = value ? new Date(value) : null;
  const date =
    directDate && !Number.isNaN(directDate.getTime())
      ? directDate
      : getDateFromDaysSinceContact(daysSinceContact);

  if (!date || Number.isNaN(date.getTime())) return "Last contacted: Not available";

  const dateText = date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const daysText = daysSinceContact == null ? "" : ` (${daysSinceContact} days ago)`;

  return `Last contacted: ${dateText}${daysText}`;
};

const CallFollowUpMembersPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [groupCallOpen, setGroupCallOpen] = useState(false);
  const [staleDays, setStaleDays] = useState(LAST_CONTACT_ALL);
  const [staleMembers, setStaleMembers] = useState([]);
  const [staleLoading, setStaleLoading] = useState(false);
  const [staleError, setStaleError] = useState("");
  const [selectAllConfirmaitonOpen, setSelectAllConfirmaitonOpen] = useState(false)

  const navigate = useNavigate()

  const theme = useTheme();

  const currentUserId = useMemo(() => localStorage.getItem("userId") || "", []);

  // const fetchMembers = async () => {
  //   try {
  //     setLoading(true);
  //     setError("");

  //     const response = await ApiCallService.get(
  //       `${process.env.REACT_APP_BASE_URL}/committeeMembers/membersByCouncil?councilId=69304eb9083479c0cf0dc0e3`
  //     );
  //     setMembers(getMembersFromResponse(response).map(normalizeMember));
  //   } catch (err) {
  //     console.error("Failed to load committee members", err);
  //     setError("Failed to load committee members");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/committeeMembers/membersByCouncil?councilId=69304eb9083479c0cf0dc0e3`
      );

       const FOUNDER = {
        "_id": "6a7585d5a0f828fc003b7463",
        "userImageURL": "public/profile-images/1784114514656-SMsirVMSprofpic (1).png",
        "userFirstName": "Shri Shantilal",
        "userLastName": "Muttha",
        "whatsAppNumber": "9823029525",
        "mobileNumber": "9823029525",
        "gender": "Male",
        "Country": {
          "$oid": "676a5efa74f99238834a5c4c"
        },
        "userRole": "SuperAdmin",
        "verified": "verified",
        "userStatus": "active",
        "filledBy": "System",
        "__v": 1,
        "adminModules": [],
        "updateApprovalStatus": "none",
        position: "Founder",
        committeeType: "Founder",
      }

      const committeeMembers =
        getMembersFromResponse(response).map(normalizeMember);

      setMembers([
        FOUNDER,
        ...committeeMembers,
      ]);

    } catch (err) {
      console.error("Failed to load committee members", err);
      setError("Failed to load committee members");
    } finally {
      setLoading(false);
    }
  };

// JUST TEST THE WORKING OG THIS FIRST THEN PROCEED WITH ANY CHANGES CHECK CONSOLE AND NEXT ACTION IS TO JUST RENDER THE CARD. THE DATA IS HERE. If Console says so...

  useEffect(() => {
    window.scrollTo({top:0, behavior:"smooth"})
    fetchMembers();
  }, []);

  useEffect(() => {
    const fetchStaleMembers = async () => {
      if (staleDays === LAST_CONTACT_ALL) {
        setStaleMembers([]);
        setStaleError("");
        return;
      }

      const memberIds = getSelectableMemberIds(members);
      if (!memberIds.length) {
        setStaleMembers([]);
        return;
      }

      try {
        setStaleLoading(true);
        setStaleError("");
        const response = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/followUp/nec-summary`,
          {
            year: new Date().getFullYear(),
            staleDays,
            memberIds: memberIds.join(","),
          }
        );

        setStaleMembers(response?.data?.members || []);
      } catch (err) {
        console.error("Failed to load not-connected members", err);
        setStaleMembers([]);
        setStaleError("Failed to load not-connected members");
      } finally {
        setStaleLoading(false);
      }
    };

    fetchStaleMembers();
  }, [members, staleDays]);

  useEffect(() => {
    const availableIds = new Set(members.map(getMemberKey).filter(Boolean));
    setSelectedMemberIds((prev) => prev.filter((id) => availableIds.has(id)));
  }, [members]);

  const staleMemberMap = useMemo(
    () => new Map(staleMembers.map((member) => [member.memberId, member])),
    [staleMembers]
  );

  const filteredMembers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return members.filter((member) => {
      if (staleDays !== LAST_CONTACT_ALL && !staleMemberMap.has(getMemberKey(member))) {
        return false;
      }

      const searchable = [
        member.userFirstName,
        member.userLastName,
        member.mobileNumber,
        member.email,
        member.position,
        member.committeeType,
        member.council,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return !query || searchable.includes(query);
    });
  }, [members, searchTerm, staleDays, staleMemberMap]);

  const sortedMembers = useMemo(() => {
    const getName = (member) =>
      `${member.userFirstName || ""} ${member.userLastName || ""}`.trim();

    const direction = sortBy.endsWith("Desc") ? -1 : 1;
    const field = sortBy.startsWith("committee") ? "committeeType" : "";

    return [...filteredMembers].sort((a, b) => {
      const left = field === "name" ? getName(a) : a.committeeType || "";
      const right = field === "name" ? getName(b) : b.committeeType || "";
      return left.localeCompare(right) * direction;
    });
  }, [filteredMembers, sortBy]);

  const groupedMembers = useMemo(() => {
    const groups = committeeSections.map((section) => ({
      ...section,
      members: [],
    }));
    const otherGroup = {
      key: "other",
      title: "Other Committee Members",
      members: [],
    };

    sortedMembers.forEach((member) => {
      const sectionKey = getMemberSectionKey(member);
      const group = groups.find(({ key }) => key === sectionKey) || otherGroup;
      group.members.push(member);
    });

    return [...groups, otherGroup].filter((group) => group.members.length > 0);
  }, [sortedMembers]);

  const allGroupedMembers = useMemo(() => {
    const groups = committeeSections.map((section) => ({
      ...section,
      members: [],
    }));
    const otherGroup = {
      key: "other",
      title: "Other Committee Members",
      members: [],
    };

    members.forEach((member) => {
      const sectionKey = getMemberSectionKey(member);
      const group = groups.find(({ key }) => key === sectionKey) || otherGroup;
      group.members.push(member);
    });

    return [...groups, otherGroup].filter((group) => group.members.length > 0);
  }, [members]);

  const selectedMemberIdSet = useMemo(
    () => new Set(selectedMemberIds),
    [selectedMemberIds]
  );

  const selectedGroupMembers = useMemo(
    () => members.filter((member) => selectedMemberIdSet.has(getMemberKey(member))),
    [members, selectedMemberIdSet]
  );

  const visibleMemberIds = useMemo(
    () => getSelectableMemberIds(sortedMembers),
    [sortedMembers]
  );
  const allMemberIds = useMemo(() => getSelectableMemberIds(members), [members]);
  const allVisibleSelected =
    visibleMemberIds.length > 0 &&
    visibleMemberIds.every((id) => selectedMemberIdSet.has(id));
  const selectedGroupLabel = useMemo(() => {
    const selectedIds = selectedMemberIds.filter(Boolean);
    if (selectedIds.length < 2) return "";

    if (sameMemberSet(selectedIds, allMemberIds)) {
      return "All Members";
    }

    const completeGroups = allGroupedMembers
      .map((group) => ({
        title: group.title,
        ids: getSelectableMemberIds(group.members),
      }))
      .filter(
        (group) =>
          group.ids.length > 0 &&
          group.ids.every((id) => selectedMemberIdSet.has(id))
      );
    const coveredIds = completeGroups.flatMap((group) => group.ids);

    if (completeGroups.length && sameMemberSet(selectedIds, coveredIds)) {
      return completeGroups.map((group) => group.title).join(", ");
    }

    return "";
  }, [allGroupedMembers, allMemberIds, selectedMemberIdSet, selectedMemberIds]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStaleDaysChange = (event) => {
    setStaleDays(event.target.value);
    setSelectedMemberIds([]);
  };

  const handleToggleMemberSelection = (member) => {
    const memberId = getMemberKey(member);
    if (!memberId) return;

    setSelectedMemberIds((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSelectMemberIds = (memberIds = []) => {
    const ids = memberIds.filter(Boolean);
    if (!ids.length) return;

    setSelectedMemberIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const handleDeselectMemberIds = (memberIds = []) => {
    const idsToRemove = new Set(memberIds.filter(Boolean));
    if (!idsToRemove.size) return;

    setSelectedMemberIds((prev) => prev.filter((id) => !idsToRemove.has(id)));
  };

  const handleToggleVisibleMembers = () => {
    if (allVisibleSelected) {
      handleDeselectMemberIds(visibleMemberIds);
      return;
    }

    handleSelectMemberIds(visibleMemberIds);
  };

  const handleClearSelection = () => {
    setSelectedMemberIds([]);
  };

  const handleRemoveGroupMember = (memberId) => {
    setSelectedMemberIds((prev) => prev.filter((id) => id !== memberId));
  };

  const handleCloseGroupCall = () => {
    setGroupCallOpen(false);
  };

  const handleGroupSaved = async () => {
    setSelectedMemberIds([]);
    await fetchMembers();
  };

  const handleConfirmSelectionChange = () => {
    handleToggleVisibleMembers();
    setSelectAllConfirmaitonOpen(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 3 },
   backgroundColor:"#fff"
      }}
    >

      <Box sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={800}>
              Add Touch-points
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.4 }}>
              Select a committee member and log call details.
            </Typography>
          </Box>

          {!loading && visibleMemberIds.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
            >
              <Button
                variant="outlined"
                // onClick={handleToggleVisibleMembers}
                onClick={() => setSelectAllConfirmaitonOpen(true)}
                sx={{
                  borderRadius: 1,
                  px: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  // bgcolor: allVisibleSelected ? theme.palette.secondary.main : theme.palette.secondary.pillColor,
                  color: "text.secondary",
                    borderColor: theme.palette.accent.followUp,
                  "&:hover": {
                    bgcolor: theme.palette.accent.followUpLight,
                  },
                }}
              >
                {allVisibleSelected ? "Clear Selection" : "Select All"}
              </Button>

              <Button 
              variant="outlined" 
              size="medium" 
              sx={{
                color:"text.secondary",
                borderColor: theme.palette.accent.followUp,
                "&:hover": {
                    bgcolor: theme.palette.accent.followUpLight,
                  },
              }}
              onClick={() => navigate("/FollowUpPage")}>
                My Touch-Points
              </Button>

              {selectedGroupMembers.length > 1 && (
                <Button
                  variant="contained"
                  startIcon={<GroupsIcon />}
                  onClick={() => setGroupCallOpen(true)}
                  sx={{
                    borderRadius: 1,
                    px: 2.4,
                    textTransform: "none",
                    fontWeight: 800,
                    bgcolor: theme.palette.accent.followUpLight,
                    "&:hover": {
                      bgcolor: theme.palette.accent.followUp,
                    },
                  }}
                >
                  Log Group Call
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Filters */}
      {/* <Card
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 1,
          border: "1px solid #e5eef9",
        }}
      > */}
        {/* <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}> */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, mobile, email, designation..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
            />

            <TextField
              select
              size="small"
              label="Filter by last contact"
              value={staleDays}
              onChange={handleStaleDaysChange}
              disabled={staleLoading}
              sx={{
                minWidth: { xs: "100%", md: 260 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
            >
              {staleDayOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* <TextField
            select
            size="small"
            label="Sort"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SortIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: { xs: "100%", md: 220 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
              },
            }}
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField> */}
        {/* </Stack> */}
      {/* </Card> */}

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
          {error}
        </Alert>
      )}

      {staleError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
          {staleError}
        </Alert>
      )}

      {staleLoading && (
        <>
          <Spinner loading={staleLoading} />
        </>
      )}

      {loading ? ( <>
      <Spinner loading={loading}/>
        <Box textAlign="center" mt={8}>
          <Typography mt={2} color="text.secondary">
            Loading committee members...
          </Typography>
        </Box>
      </>
      ) : sortedMembers.length === 0 && !staleLoading ? (
        <Card
          sx={{
            p: 5,
            textAlign: "center",
            borderRadius: 1,
            border: "1px dashed #cbd5e1",
            bgcolor: "#fff",
          }}
        >
          <PersonSearchIcon sx={{ fontSize: 58, color: "#90a4ae", mb: 1 }} />
          <Typography variant="h6" fontWeight={800}>
            No members found
          </Typography>
          <Typography color="text.secondary">
            {staleDays !== LAST_CONTACT_ALL
              ? `Everyone has been contacted in the last ${staleDays} days.`
              : "Try changing your search keyword."}
          </Typography>
        </Card>
      ) : (
        <>
          <Stack spacing={4}>
            {groupedMembers.map((group) => {
              const groupMemberIds = getSelectableMemberIds(group.members);
              const groupSelectedCount = groupMemberIds.filter((id) =>
                selectedMemberIdSet.has(id)
              ).length;
              const allGroupSelected =
                groupMemberIds.length > 0 &&
                groupSelectedCount === groupMemberIds.length;

              return (
                <Box key={group.key}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Typography variant="h6" fontWeight={800}>
                    {group.title}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Button
                      size="small"
                      variant={allGroupSelected ? "text" : "outlined"}
                      onClick={() =>
                        allGroupSelected
                          ? handleDeselectMemberIds(groupMemberIds)
                          : handleSelectMemberIds(groupMemberIds)
                      }
                      sx={{
                        borderRadius: 1,
                        textTransform: "none",
                        fontWeight: 800,
                        color: "#fff",
                        borderColor: "transparent",
                        bgcolor: theme.palette.accent.followUpLight,
                        "&:hover": {
                          bgcolor: theme.palette.accent.followUp,
                        },
                      }}
                    >
                      {allGroupSelected ? "Clear Committee" : "Select Committee"}
                    </Button>

                    <Chip
                      size="small"
                      label={`${groupSelectedCount}/${group.members.length} selected`}
                      sx={{
                        borderRadius: 1,
                        fontWeight: 700,
                        bgcolor:"#eef6ff",
                        color: theme.palette.COLORS.option2,
                      }}
                    />

                    <Chip
                      size="small"
                      label={`${group.members.length} member${
                        group.members.length === 1 ? "" : "s"
                      }`}
                      sx={{
                        borderRadius: 1,
                        fontWeight: 700,
                        bgcolor: "#eef6ff",
                        color: theme.palette.COLORS.option2,
                      }}
                    />
                  </Stack>
                </Stack>

                <Grid container spacing={2.5}>
                  {group.members.map((member, index) => {
                    const fullName =
                      `${member.userFirstName} ${member.userLastName}`.trim() ||
                      "Committee member";

                    return (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={4}
                        key={member.userId || member._id}
                      >
                        <Card
                          sx={{
                            height: "100%",
                            p: 2.5,
                            borderRadius: 2,
                            position: "relative",
                            overflow: "hidden",
                            bgcolor: selectedMemberIdSet.has(getMemberKey(member))
                              ? "#f0f9ff"
                              : "#fff",
                            border: selectedMemberIdSet.has(getMemberKey(member))
                              ? `1px solid ${theme.palette.accent.followUp}`
                              : "1px solid #e5eef9",
                            animation: `fadeUp 0.45s ease ${index * 0.04}s both`,
                            transition: "all 0.28s ease",
                            "@keyframes fadeUp": {
                              from: {
                                opacity: 0,
                                transform: "translateY(18px)",
                              },
                              to: {
                                opacity: 1,
                                transform: "translateY(0)",
                              },
                            },
                            "&:hover": {
                              transform: "translateY(-5px)",
                              boxShadow: "0 18px 45px rgba(15,23,42,0.13)",
                              borderColor: "#bbdefb",
                            },
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: 5,
                              // background:
                              //   "linear-gradient(90deg, #004cff, #A92FDD)",
                            },
                          }}
                        >
                          <Stack direction="row" spacing={1.4} alignItems="center">
                            <Tooltip title={getMemberKey(member) === currentUserId ? "Cannot select yourself for a group call" : "Select for group call"}>
                              <Checkbox
                                checked={selectedMemberIdSet.has(getMemberKey(member))}
                                onChange={() => handleToggleMemberSelection(member)}
                                disabled={getMemberKey(member) === currentUserId}
                                inputProps={{
                                  "aria-label": `Select ${fullName} for group call`,
                                }}
                                sx={{
                                  color: theme.palette.accent.followUp,
                                  backgroundColor: "#fff",
                                  borderRadius: 1,
                                  flexShrink: 0,
                                  p: 0.45,
                                  "&.Mui-checked": {
                                    color: theme.palette.accent.followUpLight,
                                    backgroundColor:"transparent"
                                  },
                                }}
                              />
                            </Tooltip>

                            <Avatar
                              src={getPublicS3Url(member.userImageURL) || dummyImage}
                              alt={fullName}
                              sx={{
                                width: 66,
                                height: 66,
                                fontWeight: 800,
                                bgcolor: theme.palette.accent.followUpWhite,
                                border: "3px solid #fff",
                                // boxShadow: "0 8px 20px rgba(25,118,210,0.22)",
                              }}
                            >
                              {getInitials(
                                member.userFirstName,
                                member.userLastName
                              )}
                            </Avatar>

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                fontWeight={800}
                                noWrap
                                title={fullName}
                                sx={{ fontSize: 16 }}
                              >
                                {fullName}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                title={member.position || "-"}
                                sx={{ mt: 0.3 }}
                              >
                                {member.position || "-"}
                              </Typography>

                              {/* <Stack
                                direction="row"
                                spacing={0.8}
                                alignItems="center"
                                sx={{ mt: 1 }}
                              >
                                <Chip
                                  size="small"
                                  label={
                                    member.committeeType ||
                                    member.council ||
                                    "Committee member"
                                  }
                                  sx={{
                                    maxWidth: 170,
                                    height: 24,
                                    borderRadius: 1,
                                    fontWeight: 700,
                                    bgcolor: "#057aff",
                                    color: theme.palette.secondary.pillColor,
                                    "& .MuiChip-label": {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    },
                                  }}
                                />
                              </Stack> */}

                              {member.mobileNumber && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ display: "block", mt: 0.8 }}
                                >
                                  {member.mobileNumber}
                                </Typography>
                              )}

                              {staleDays !== LAST_CONTACT_ALL && (
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{ display: "block", mt: 0.6, color: "#a44900", fontWeight: 800 }}
                                  title={formatLastContact(
                                    getLastContactValue(staleMemberMap.get(getMemberKey(member))),
                                    staleMemberMap.get(getMemberKey(member))?.daysSinceContact
                                  )}
                                >
                                  {formatLastContact(
                                    getLastContactValue(staleMemberMap.get(getMemberKey(member))),
                                    staleMemberMap.get(getMemberKey(member))?.daysSinceContact
                                  )}
                                </Typography>
                              )}
                            </Box>

                            <Tooltip title={getMemberKey(member) === currentUserId ? "Cannot log touch-point with yourself" : "Log touch-point"}>
                              <span>
                                <IconButton
                                  aria-label={`Log touch-point for ${fullName}`}
                                  onClick={() => setSelectedMember(member)}
                                  disabled={getMemberKey(member) === currentUserId}
                                  sx={{
                                    bgcolor: getMemberKey(member) === currentUserId ? "#eeeeee" : "#e3f2fd",
                                    color: getMemberKey(member) === currentUserId ? "#999999" : theme.palette.secondary.pillColor,
                                    transition: "0.25s ease",
                                    "&:hover": {
                                      bgcolor: "#bbdefb",
                                      // bgcolor: getMemberKey(member) === currentUserId ? "#eeeeee" : "#bbdefb",
                                      transform: getMemberKey(member) === currentUserId ? "none" : "rotate(-2.5deg) scale(1.1)",
                                    },
                                  }}
                                >
                                  <PermPhoneMsgIcon sx={{color:theme.palette.accent.followUpLight}}/>
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                </Box>
              );
            })}
          </Stack>
        </>
      )}

      <Dialog
        open={selectAllConfirmaitonOpen}
        onClose={() => setSelectAllConfirmaitonOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {allVisibleSelected ? "Clear Selection?" : "Select All Members?"}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {allVisibleSelected
              ? "This will clear all currently selected members. Any manual selections you made will be removed."
              : "This will select all visible members. Do you want to continue?"}
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSelectAllConfirmaitonOpen(false)}
            variant="text"
            sx={{
              color: theme.palette.accent.followUpLight,
              "&:hover":{
                backgroundColor:theme.palette.accent.followUpLight
              }
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirmSelectionChange}
            variant="contained"
            // color={allVisibleSelected ? "error" : "primary"}
            sx={{
              color: "white",
              backgroundColor:theme.palette.accent.followUp,
              "&:hover":{
                backgroundColor:theme.palette.accent.followUpLight
              }
            }}
          >
            {allVisibleSelected ? "Clear Selection" : "Select All"}
          </Button>
        </DialogActions>
      </Dialog>

      <CallFollowUpModal
        open={Boolean(selectedMember)}
        onClose={() => setSelectedMember(null)}
        member={selectedMember}
        onSaved={fetchMembers}
      />
      <CallFollowUpModal
        open={groupCallOpen}
        onClose={handleCloseGroupCall}
        groupMembers={selectedGroupMembers}
        groupLabel={selectedGroupLabel}
        onRemoveGroupMember={handleRemoveGroupMember}
        onSaved={handleGroupSaved}
      />
    </Box>
  );
};

export default CallFollowUpMembersPage;
